import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { toClientTypeTransformer, toSimpleClientTypeTransformer } from '@app/core/state/transformers/transformers';
import { showTodoDialog } from '@app/shared/misc/showTodoDialog';
import { TranslocoService } from '@jsverse/transloco';
import { DialogService } from 'primeng/dynamicdialog';
import { filter, Observable, BehaviorSubject } from 'rxjs';
import { Client } from 'src/app/core/entity/client';
import { Todo } from 'src/app/core/entity/todo';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { NotesService } from 'src/app/core/services/notes.service';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';
import { TodoSelectableStates } from 'src/app/core/state/types/todo.types';
import { translateColumnHeaders } from 'src/app/shared/components/table/misc/translate-column-header';
import { TodayDate } from 'src/app/shared/misc/today.date';
import { TableColumn } from '../../../../table/interfaces/table-column';

@Component({
  selector: 'app-client-todos',
  templateUrl: './client.todos.component.html',
  providers: [getTranslationProvider('ClientTodosComponent')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientTodosComponent extends TranslatableComponent {
  @Output() updateEmitter = new EventEmitter<boolean>();

  @Input()
  get client() {
    return this._client;
  }
  set client(client: Client) {
    this._client = client;
    if (this._client.id > 0) {
      this.loadClientTodos();
    } else {
      this.clientTodosSubject.next([]);
    }
  }

  clientTodos$: Observable<Todo[]>;
  pendingDeletion: Todo = new Todo();
  columns$: Observable<TableColumn[]>;
  displayDeleteDialog = false;

  private clientTodosSubject = new BehaviorSubject<Todo[]>([]);
  private _client: Client;
  private todayDateWithActiveLang = new TodayDate();
  private columns: TableColumn[] = [
    { field: 'type', header: 'type', width: '90px' },
    { field: 'assignment', header: 'assignment', width: '110px' },
    { field: 'entryDate', header: 'entryDate', width: '150px' },
    { field: 'text', header: 'text' },
    { field: 'creator.initials', header: 'createdBy', width: '120px' },
    { field: 'usernames', header: 'user', width: '120px' },
    { field: 'deadline', header: 'deadline', width: '105px' },
    { field: 'status', header: 'status', width: '125px', sortable: false },
    { field: 'action', header: '', width: '40px', sortable: false },
  ];

  constructor(
    private notesService: NotesService,
    private dialogService: DialogService,
    private translocoService: TranslocoService,
  ) {
    super();
    this.columns$ = this.onTranslationChangeMapTo(translateColumnHeaders(this.columns));
    this.clientTodos$ = this.clientTodosSubject.asObservable();
  }

  override onTranslationsLoaded() {
    this.todayDateWithActiveLang = new TodayDate(this.getActiveLanguage());
  }

  changeStateAndSave(newStateName: string, todo: Todo) {
    if (todo.state.name === newStateName) {
      return;
    }

    const state = this.getStateFromEvent(newStateName);
    this.notesService.addTodo({ ...todo, state }).subscribe(() => {
      this.store.dispatch(
        ToastActions.showInfoMessage({ summary: this.translate('saved'), detail: this.translate('statusChanged') }),
      );
      this.loadClientTodos();
    });
  }

  tryDeleteTodo(toDelete: Todo) {
    this.displayDeleteDialog = true;
    this.pendingDeletion = toDelete;
  }

  afterDeleteDialog(del: boolean) {
    if (del) {
      this.notesService.deleteTodo(this.pendingDeletion.id).subscribe(() => {
        this.store.dispatch(
          ToastActions.showInfoMessage({
            summary: this.translate('removed'),
            detail: this.translate('noteHasBeenRemoved'),
          }),
        );
        this.loadClientTodos();
        this.pendingDeletion = new Todo();
      });
    } else {
      this.pendingDeletion = new Todo();
    }
    this.displayDeleteDialog = false;
  }

  showExistingTodo(model: Todo) {
    const deps = {
      notesService: this.notesService,
      dialogService: this.dialogService,
      translationService: this.translocoService,
      store: this.store,
    };
    showTodoDialog({ deps, id: model.id })
      .pipe(filter((result) => Boolean(result?.todo)))
      .subscribe(() => {
        this.loadClientTodos();
      });
  }

  showNewTodo() {
    const deps = {
      notesService: this.notesService,
      dialogService: this.dialogService,
      translationService: this.translocoService,
      store: this.store,
    };
    showTodoDialog({
      deps,
      id: -1,
      client: toSimpleClientTypeTransformer.transform(toClientTypeTransformer.transform(this._client)),
    })
      .pipe(filter((result) => Boolean(result?.todo)))
      .subscribe(() => {
        this.loadClientTodos();
      });
  }

  isDisabledSelector(todo: Todo) {
    return todo.users === undefined || todo.users.length === 0;
  }

  public toLocalDateFormat(date: string): string {
    return date ? this.todayDateWithActiveLang.formatDateByLocale(new Date(date)) : '';
  }

  private loadClientTodos() {
    this.notesService.getByClientId(this._client.id).subscribe((todos) => {
      this.clientTodosSubject.next(this.preprocessTodos(todos));
    });
  }

  private preprocessTodos(todos: Todo[]) {
    return todos
      .filter((t) => Boolean(t))
      .map((todo) => ({
        ...todo,
        usernames: (todo.users || []).map((user) => user.initials).join(', '),
        assignmentname: todo.assignment?.name || '', // not sure what this is for
        statename: todo.state?.name || '', // not sure what this is for
      }));
  }

  private getStateFromEvent(stateName: string) {
    return TodoSelectableStates.find((s) => s.name === stateName) ?? TodoSelectableStates[0];
  }
}
